import { z } from 'zod';
import {
  CAUSE_CATEGORY,
  LIMITS,
  PRIORITY,
  REJECT_REASON,
  TICKET_STATUS,
  TILE,
  VISIBILITY,
} from './constants.ts';
import { qrCodeSchema } from './machine.ts';

const text = (min: number, max: number, message?: string) =>
  z.string().trim().min(min, message).max(max);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * Pflicht beim Melden (F16): Maschine (oder „ohne Maschine/Gebäude“), eine Kachel
 * und ein Satz ODER ein Foto. Fotos werden nach dem Anlegen einzeln hochgeladen –
 * `photoCount` kündigt sie an, damit eine Meldung nur mit Foto zulässig ist.
 */
const reportCore = {
  tile: z.enum(TILE),
  description: optionalText(LIMITS.descriptionMaxChars),
  photoCount: z.number().int().min(0).max(LIMITS.photosOnReport).default(0),
};

function sentenceOrPhoto(v: { description?: string | null; photoCount: number }) {
  return Boolean(v.description && v.description.length >= 3) || v.photoCount > 0;
}
const SENTENCE_OR_PHOTO = 'Bitte einen kurzen Satz schreiben oder ein Foto anhängen.';

/** Angemeldete Meldung über Web oder QR (F18). */
export const createTicketSchema = z
  .object({
    ...reportCore,
    machineId: z.uuid().nullable().optional(),
    /** QR-Scan ohne Netz: Der Code ist alles, was vorliegt, der Server löst ihn beim Senden auf. */
    machineCode: qrCodeSchema.nullable().optional(),
    /** „ohne Maschine/Gebäude“ – Ticket landet in „Prüfen“ (needs_review). */
    withoutMachine: z.boolean().default(false),
    origin: z.enum(['web', 'qr']).default('web'),
    title: optionalText(LIMITS.titleMaxChars),
  })
  .refine(sentenceOrPhoto, { message: SENTENCE_OR_PHOTO, path: ['description'] })
  .refine((v) => Boolean(v.machineId) || Boolean(v.machineCode) || v.withoutMachine, {
    message: 'Bitte eine Maschine wählen oder „ohne Maschine/Gebäude“ ankreuzen.',
    path: ['machineId'],
  });
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

const reporterName = z
  .string()
  .trim()
  .min(2, 'Bitte deinen Namen angeben.')
  .max(80, 'Der Name ist zu lang.');

/**
 * Schnellmeldung ohne Login per QR (F6, §9.4).
 * `website` ist ein Honigtopf-Feld: Menschen sehen es nicht, Bots füllen es.
 */
export const quickReportSchema = z
  .object({
    ...reportCore,
    code: z.string().trim().toUpperCase().min(6).max(6),
    name: reporterName,
    email: z.email('Bitte eine gültige E-Mail-Adresse angeben.').nullable().optional(),
    website: z.string().max(0).optional().default(''),
  })
  .refine(sentenceOrPhoto, { message: SENTENCE_OR_PHOTO, path: ['description'] });
export type QuickReportInput = z.infer<typeof quickReportSchema>;

/** Meldung vom Hallen-Tablet (Kiosk-Board, ohne Login). */
export const kioskReportSchema = z
  .object({
    ...reportCore,
    machineId: z.uuid(),
    name: reporterName,
    website: z.string().max(0).optional().default(''),
  })
  .refine(sentenceOrPhoto, { message: SENTENCE_OR_PHOTO, path: ['description'] });
export type KioskReportInput = z.infer<typeof kioskReportSchema>;

/** „Schon gemeldet? Hier anhängen“ (F19) – öffentlicher Kommentar, auch anonym. */
export const quickCommentSchema = z.object({
  name: reporterName,
  body: text(2, LIMITS.quickCommentMaxChars, 'Bitte einen kurzen Hinweis schreiben.'),
  website: z.string().max(0).optional().default(''),
});

const version = z.number().int().min(1);

/**
 * Übergänge des Automaten (Plan §7). Je Ziel eigene Pflichtfelder; die
 * Server-Prüfung (Rolle, Standort, Guards) steht in `tickets/state.ts`.
 */
export const transitionSchema = z.discriminatedUnion('to', [
  z.object({
    to: z.literal('accepted'),
    version,
    /** Nur Leitung darf jemand anderen eintragen; sonst Selbstzuweisung. */
    assigneeUserId: z.uuid().optional(),
    /** Bei „Prüfen“-Tickets: Maschine nachtragen oder „ohne Maschine“ bestätigen. */
    machineId: z.uuid().optional(),
    confirmNoMachine: z.boolean().optional(),
  }),
  z.object({
    to: z.literal('in_progress'),
    version,
    /** P1: „Maschine gesichert/freigeschaltet?“ beim Arbeitsbeginn (LOTO). */
    lockedOut: z.boolean().optional(),
    comment: optionalText(LIMITS.commentMaxChars),
  }),
  z.object({
    to: z.literal('waiting_part'),
    version,
    waitingNote: text(3, 500, 'Bitte notieren, welches Teil fehlt.'),
    inforItemNo: optionalText(40),
    waitingUntil: z.iso.date().nullable().optional(),
  }),
  z
    .object({
      to: z.literal('waiting_external'),
      version,
      waitingContactId: z.uuid().nullable().optional(),
      waitingNote: optionalText(500),
      waitingUntil: z.iso.date().nullable().optional(),
    })
    .refine((v) => Boolean(v.waitingContactId) || Boolean(v.waitingNote), {
      message: 'Bitte den Dienstleister auswählen oder als Notiz eintragen.',
      path: ['waitingNote'],
    }),
  z.object({
    to: z.literal('waiting_reporter'),
    version,
    question: text(3, LIMITS.commentMaxChars, 'Bitte die Rückfrage formulieren.'),
  }),
  z.object({
    to: z.literal('resolved'),
    version,
    causeCategory: z.enum(CAUSE_CATEGORY),
    resolutionText: text(3, LIMITS.commentMaxChars, 'Bitte die Maßnahme beschreiben.'),
    productionStop: z.boolean(),
    machineRunningAt: z.iso.datetime({ offset: true }).nullable().optional(),
    releasedByUserId: z.uuid().nullable().optional(),
    sparePartNote: optionalText(500),
    inforItemNo: optionalText(40),
  }),
  z.object({
    to: z.literal('closed'),
    version,
  }),
  z
    .object({
      to: z.literal('rejected'),
      version,
      reason: z.enum(REJECT_REASON),
      /** Freiwillig; steht im Ticket und geht an den Melder (außer bei Spam). */
      comment: optionalText(2000),
      duplicateOfNumber: z.number().int().min(1000).optional(),
    })
    .refine((v) => v.reason !== 'duplicate' || Boolean(v.duplicateOfNumber), {
      message: 'Bei einer Dublette bitte die Nummer des Original-Tickets angeben.',
      path: ['duplicateOfNumber'],
    }),
  /** Wiederöffnen aus `closed`/`rejected` (nur Leitung, 30 Tage). */
  z.object({
    to: z.literal('reopen'),
    version,
    comment: text(3, 2000, 'Bitte begründen, warum das Ticket wieder geöffnet wird.'),
  }),
]);
export type TransitionInput = z.infer<typeof transitionSchema>;
export type TransitionTarget = TransitionInput['to'];

export const commentSchema = z.object({
  body: text(1, LIMITS.commentMaxChars, 'Der Kommentar ist leer.'),
  /** `public` = an den Melder (F29): steht im Ticket, mit Microsoft-Konto auch als Mail im Thread. */
  visibility: z.enum(VISIBILITY).default('internal'),
  /** Erwähnte Kollegen (@Name): werden benachrichtigt, wenn sie das Ticket sehen dürfen. */
  mentions: z.array(z.uuid()).max(LIMITS.mentionsPerComment).default([]),
});
export type CommentInput = z.infer<typeof commentSchema>;

/** Formularfelder beim Upload neben der Datei. Für Melder wertet der Server `visibility` nicht aus. */
export const attachmentUploadSchema = z.object({
  visibility: z.enum(VISIBILITY, { error: 'Bitte „Intern“ oder „An Melder“ wählen.' }).optional(),
});

export const priorityChangeSchema = z.object({
  version,
  priority: z.enum(PRIORITY),
  reason: optionalText(500),
});

export const assigneeChangeSchema = z.object({
  version,
  assigneeUserId: z.uuid(),
});

export const machineChangeSchema = z.object({
  version,
  machineId: z.uuid(),
});

export const lockoutChangeSchema = z.object({
  version,
  lockedOut: z.boolean(),
});

export const replyUnblockSchema = z.object({ version });

/**
 * Ansichten der Ticketliste (wie InvGate): Offen, Nicht zugewiesen, Mir zugewiesen,
 * Wartend, Prüfen, Behoben (wartet auf Bestätigung), Erledigt, Alle.
 */
export const TICKET_TAB = ['open', 'unassigned', 'mine', 'involved', 'waiting', 'review', 'resolved', 'done', 'all'] as const;
export type TicketTab = (typeof TICKET_TAB)[number];

/** Reiter mit abgeschlossenen Tickets. Dort gibt es keine Sicht je Person (§14.1). */
export const HISTORY_TABS: readonly TicketTab[] = ['resolved', 'done', 'all'];

/** Die persönlichen Reiter „Mir zugewiesen“ und „Hinzugezogen“ gehören in keine Datei (§14.1). */
export const EXPORT_TABS = ['open', 'unassigned', 'waiting', 'review', 'resolved', 'done', 'all'] as const satisfies readonly TicketTab[];

export const TICKET_SORT = ['priority', 'updated', 'created', 'number'] as const;
export type TicketSort = (typeof TICKET_SORT)[number];

export const ticketListQuerySchema = z.object({
  tab: z.enum(TICKET_TAB).default('open'),
  siteId: z.uuid().optional(),
  areaId: z.uuid().optional(),
  machineId: z.uuid().optional(),
  teamId: z.uuid().optional(),
  /** Bearbeiter: UUID, `me` oder `none` (nicht zugewiesen). */
  assignee: z.union([z.uuid(), z.literal('me'), z.literal('none')]).optional(),
  status: z.enum(TICKET_STATUS).optional(),
  priority: z.enum(PRIORITY).optional(),
  q: z.string().trim().max(100).optional(),
  sort: z.enum(TICKET_SORT).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

/** Der CSV-Export filtert nie nach Bearbeitern und liefert immer alle Treffer (§14.1). */
export const ticketExportQuerySchema = ticketListQuerySchema.omit({ assignee: true, page: true, pageSize: true }).extend({
  tab: z.enum(EXPORT_TABS, { error: 'Diese Ansicht lässt sich nicht exportieren.' }).default('open'),
});

/**
 * Zuweisung wie InvGate „Reassign“: Team (Pflicht) und optional eine Person,
 * dazu eine interne Notiz für den Kontext.
 */
export const reassignSchema = z.object({
  version,
  teamId: z.uuid(),
  assigneeUserId: z.uuid().nullable().optional(),
  comment: optionalText(2000),
});
export type ReassignInput = z.infer<typeof reassignSchema>;

/** Kollegen hinzuziehen, auch aus dem anderen Werk (InvGate: Collaborators). */
export const collaboratorAddSchema = z.object({
  version,
  userId: z.uuid(),
  comment: optionalText(1000),
});
export const collaboratorRemoveSchema = z.object({ version });

export const teamInputSchema = z.object({
  siteId: z.uuid(),
  name: z.string().trim().min(2, 'Bitte einen Namen angeben.').max(80),
  description: optionalText(300),
  isDefault: z.boolean().default(false),
  isActive: z.boolean().default(true),
  memberIds: z.array(z.uuid()).default([]),
});
export type TeamInput = z.infer<typeof teamInputSchema>;

export const savedViewSchema = z.object({
  name: z.string().trim().min(2, 'Bitte einen Namen angeben.').max(60),
  query: z.record(z.string(), z.string().max(200)),
});
export type TicketListQuery = z.infer<typeof ticketListQuerySchema>;
export type TicketExportQuery = z.infer<typeof ticketExportQuerySchema>;

/** Antwort des Melders aus der Behoben-Mail oder der App (F28). */
export const confirmSchema = z.object({
  token: z.string().min(20).max(400),
  action: z.enum(['running', 'not_running']),
  comment: optionalText(1000),
});
